import { makeBoardKey } from "@components/modules/resource-planning/tasks/kanban/utils";
import { t } from "@lingui/macro";
import {
  DeleteTaskDocument,
  type DeleteTaskMutation,
  type DeleteTaskMutationVariables,
  type ListingInternalTaskFragment,
  MoveTaskOnBoardDocument,
  type MoveTaskOnBoardInput,
  type MoveTaskOnBoardMutation,
  type MoveTaskOnBoardMutationVariables,
  SortOrder,
  SqlOperator,
  StarsStarableTypeEnum,
  type TaskBaseFragment,
  TaskBoardColumnCountsDocument,
  type TaskBoardColumnCountsQuery,
  type TaskBoardColumnCountsQueryVariables,
  type TaskBoardColumnTasksQueryVariables,
  TaskBoardGroupByEnum,
  TaskOrderByColumnEnum,
  TaskSystemStatusEnum,
  TaskWhereColumn,
  UpdateTaskCurrentAssigneeDocument,
  type UpdateTaskCurrentAssigneeMutation,
  type UpdateTaskCurrentAssigneeMutationVariables,
  UpdateTaskDocument,
  type UpdateTaskMutation,
  type UpdateTaskMutationVariables,
  UpdateTaskPriorityDocument,
  type UpdateTaskPriorityMutation,
  type UpdateTaskPriorityMutationVariables,
} from "@src/__generated__/graphql";
import type { TTaskPageVariant } from "@src/components/modules/resource-planning/tasks";
import {
  BOARD_COLUMN_WIDTH,
  BOARD_COMPLETE_COLUMN_KEY,
  BOARD_GROUP_BY_STORAGE_KEY,
  BOARD_UNASSIGNED_VALUE,
} from "@src/components/modules/resource-planning/tasks/kanban/constants";
import {
  type TaskBoardColumnSource,
  TaskBoardColumnStore,
} from "@src/components/modules/resource-planning/tasks/kanban/store/column";
import { TaskBoardDragStore } from "@src/components/modules/resource-planning/tasks/kanban/store/drag";
import { TaskBoardSettingsStore } from "@src/components/modules/resource-planning/tasks/kanban/store/settings";
import type {
  BoardMoveBlockedNotice,
  BoardReorderBlocker,
} from "@src/components/modules/resource-planning/tasks/kanban/types";
import { TasksBaseStore } from "@src/components/modules/resource-planning/tasks/store/base-store";
import ModalConfirm from "@src/components/ui-kit/Modals/ModalConfirm/ModalConfirm";
import { DuplicateTaskModal } from "@src/components/widgets/Modals/DuplicateTaskModal";
import { ModalTaskEdit } from "@src/components/widgets/Modals/ModalTask/ModalTaskEdit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { TaskFormModalOpenOptions } from "@src/stores/TaskFormModalStore";
import { FormMode } from "@src/utils/types";
import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { now as tickingNow } from "mobx-utils";
import { createElement } from "react";

export class TasksKanbanStore extends TasksBaseStore {
  settingsStore: TaskBoardSettingsStore;
  dragStore: TaskBoardDragStore;

  @observable.ref tab: TTaskPageVariant = "all";
  /** The grouping the user picked, kept even on a tab that does not offer it. */
  @observable.ref private selectedGroupBy: TaskBoardGroupByEnum =
    TaskBoardGroupByEnum.TaskStatus;
  /**
   * Columns loaded so far — the API serves them in viewport-sized pages, so
   * this is also everything the board renders.
   */
  @observable.ref columns: TaskBoardColumnStore[] = [];
  /** How many columns fit the board viewport; one page. */
  @observable.ref
  private columnBatchSize = 0;
  @observable
  isSelectMode = false;
  /** Wall-clock start for the timer this board session started, for live elapsed. */
  @observable
  trackingStartedAtMs: number | null = null;
  /** Card waiting on the next page before its kebab "Move down" can run. */
  @observable.ref
  movingTaskId: string | null = null;

  /** Anchor for shift-click range selection, valid only within its column. */
  private selectAnchor?: { columnKey: string; taskId: string };

  private columnCountsFetcher = new FetchHelper<
    TaskBoardColumnCountsQuery,
    TaskBoardColumnCountsQueryVariables
  >(TaskBoardColumnCountsDocument);

  /** Own fetcher so a keyed refresh neither waits for nor blocks column paging. */
  private columnCountsByKeysFetcher = new FetchHelper<
    TaskBoardColumnCountsQuery,
    TaskBoardColumnCountsQueryVariables
  >(TaskBoardColumnCountsDocument);

  /**
   * Whenever the user moves tasks between columns, column counts recalculation is
   * requested and can be refetched for the same column multiple times. The versions
   * keep the newest version of that specific column updated.
   */
  private columnCountsVersions = new Map<string, number>();
  private columnCountsVersion = 0;

  /**
   *
   *
   * Page load in flight. An init aborts it, a next page waits for it. Own
   * signals also keep the app-wide aborter link from cancelling page loads
   * and keyed refreshes of each other — they share one operation name.
   */
  private columnsAbortController?: AbortController;

  private moveTaskMutator = new MutationHelper<
    MoveTaskOnBoardMutation,
    MoveTaskOnBoardMutationVariables
  >(MoveTaskOnBoardDocument);

  private updateCurrentAssigneeMutator = new MutationHelper<
    UpdateTaskCurrentAssigneeMutation,
    UpdateTaskCurrentAssigneeMutationVariables
  >(UpdateTaskCurrentAssigneeDocument);

  private updateTaskMutator = new MutationHelper<
    UpdateTaskMutation,
    UpdateTaskMutationVariables
  >(UpdateTaskDocument);

  private updateTaskPriorityMutator = new MutationHelper<
    UpdateTaskPriorityMutation,
    UpdateTaskPriorityMutationVariables
  >(UpdateTaskPriorityDocument);

  private deleteTaskMutator = new MutationHelper<
    DeleteTaskMutation,
    DeleteTaskMutationVariables
  >(DeleteTaskDocument);

  constructor(appStore: AppStore) {
    // The board pages over columns, so its page size is a column count and
    // must not land in the listing's rows-per-page.
    super(appStore, "tasksKanbanColumns");
    makeObservable(this);
    this.filtersStore.setViewStore(this);
    // A bulk edit may touch any loaded column.
    this.bulkActionsStore.setOnSuccess(() =>
      this.refreshColumns(this.columnKeys),
    );
    this.dragStore = new TaskBoardDragStore(this, appStore);
    this.settingsStore = new TaskBoardSettingsStore({
      onSortChange: () => {
        this.clearSelection();
        this.fetchTasks();
      },
      onColumnShown: (columnKey) => {
        const column = this.columns.find((c) => c.key === columnKey);
        if (column && !column.isCollapsed) column.reload();
      },
    });
    this.selectedGroupBy = this.readPersistedGroupBy();
    this.resetPagination();

    // Any filter change — tab filters, search, the filter panel — changes
    // every count. Show done tasks only touches the complete column of the
    // status board; other groupings have done tasks in every column.
    reaction(
      () => [this.filtersKeyWithoutHideDone, this.filtersStore.hideDoneTasks],
      ([filtersKey], [previousFiltersKey]) => {
        if (
          filtersKey === previousFiltersKey &&
          this.groupBy === TaskBoardGroupByEnum.TaskStatus
        ) {
          void this.refreshCompleteColumn();
          return;
        }

        void this.refreshColumns();
      },
      { equals: comparer.structural },
    );
  }

  /** The picked grouping, or Status on a tab that does not offer it. */
  @computed
  get groupBy(): TaskBoardGroupByEnum {
    if (
      this.tab === "my-current" &&
      this.selectedGroupBy === TaskBoardGroupByEnum.CurrentAssignee
    ) {
      return TaskBoardGroupByEnum.TaskStatus;
    }

    return this.selectedGroupBy;
  }

  @computed
  get boardKey(): string {
    return makeBoardKey(this.tab, this.groupBy);
  }

  @computed
  get isFetchingColumnCounts(): boolean {
    return this.columnCountsFetcher.isLoading.value;
  }

  @computed
  get visibleColumns(): TaskBoardColumnStore[] {
    return this.columns.filter(
      (column) => !this.settingsStore.isHidden(column.key),
    );
  }

  @computed
  get hiddenColumns(): TaskBoardColumnStore[] {
    return this.columns.filter((column) =>
      this.settingsStore.isHidden(column.key),
    );
  }

  @computed
  get canLoadMoreColumns(): boolean {
    return this.filtersStore.pagination.canLoadMore;
  }

  @computed
  private get doneStatuses() {
    return this.filtersStore.taskStatuses.filter(
      (status) => status.system_status === TaskSystemStatusEnum.Done,
    );
  }

  @computed
  private get doneStatusIds(): string[] {
    return this.doneStatuses.map((status) => status.id);
  }

  /** The complete column carries the workspace's own name for the done status. */
  @computed
  private get completeColumnLabel(): string {
    const names = [...new Set(this.doneStatuses.map(({ name }) => name))];

    return names.length ? names.join(" / ") : t`Complete`;
  }

  /**
   * Without `keys` the next page of columns (or the first one on `init`).
   * With `keys` only the counts of those loaded columns — paging, column set
   * and order stay untouched.
   */
  @action.bound
  async fetchColumns(
    options: { init?: boolean; keys?: string[] } = {},
  ): Promise<void> {
    if (options.keys) {
      await this.fetchColumnsByKeys(options.keys);
      return;
    }

    // A next page waits for the one in flight; an init replaces it.
    if (!options.init && this.columnsAbortController) return;

    if (options.init) {
      // Initial fetching should reset pagination
      this.resetPagination();
    }

    if (this.filtersStore.pagination.perPage <= 0) return;

    this.columnsAbortController?.abort(
      "A newer board page load replaced this one.",
    );
    const controller = new AbortController();
    this.columnsAbortController = controller;

    try {
      // Load settings for current board
      await this.settingsStore.fetch(this.boardKey);
      if (controller.signal.aborted) return;

      const { currentPage, perPage } = this.filtersStore.pagination;
      const version = ++this.columnCountsVersion;

      const [data, error] = await this.columnCountsFetcher.fetch(
        {
          filters: this.countsQueryFilters,
          groupBy: this.groupBy,
          // Always on the board — while done tasks are hidden it is disabled,
          // and the hidden done tasks leave it with zero counts.
          includeComplete: true,
          first: perPage,
          page: currentPage,
        },
        controller.signal,
      );

      // A newer init (board switch, done toggle, resize) owns the columns.
      if (controller.signal.aborted) return;
      if (error || !data.taskBoardColumnCounts) return;

      this.filtersStore.pagination.setFromPaginatorInfo(
        data.taskBoardColumnCounts.paginatorInfo,
      );

      this.setColumns(this.toColumnSources(data.taskBoardColumnCounts.data), {
        append: !options.init,
        version,
      });

      if (!this.filtersStore.pagination.canLoadMore) return;
      this.filtersStore.pagination.inc();
    } finally {
      if (this.columnsAbortController === controller) {
        this.columnsAbortController = undefined;
      }
    }
  }

  /**
   * With `keys` only those columns: fresh counts in one request and fresh
   * cards where they show any — for mutations, which know what they touched.
   * Paging stays, so a board scrolled three pages in does not snap back.
   * Without keys the board starts over from the first page — filters or the
   * tab changed, so every column may have, and so may the column set.
   */
  @action.bound
  async refreshColumns(keys?: string[]): Promise<void> {
    if (keys) {
      await Promise.all([
        this.fetchColumns({ keys }),
        this.fetchVisibleColumnTasks(keys),
      ]);
      return;
    }

    await this.fetchColumns({ init: true });
    await this.fetchVisibleColumnTasks();
  }

  @computed
  get columnKeys(): string[] {
    return this.columns.map((column) => column.key);
  }

  /** Loaded columns currently showing the task — where a change to it lands. */
  private columnKeysOfTask(taskId: string): string[] {
    return this.columns
      .filter((column) => column.tasks.some((task) => task.id === taskId))
      .map((column) => column.key);
  }

  /** Done statuses have no column of their own — they live in `complete`. */
  private statusColumnKey(statusId: string): string {
    return this.doneStatusIds.includes(statusId)
      ? BOARD_COMPLETE_COLUMN_KEY
      : statusId;
  }

  /** Marks the columns as having a newer counts request than any in flight. */
  claimColumnCounts(keys: string[]): number {
    const version = ++this.columnCountsVersion;
    for (const key of keys) this.columnCountsVersions.set(key, version);

    return version;
  }

  private isLatestColumnCounts(key: string, version: number): boolean {
    return (this.columnCountsVersions.get(key) ?? 0) <= version;
  }

  /**
   * `version` is claimed by the caller when the columns changed optimistically
   * before the mutation — responses sent before the mutation then cannot
   * overwrite the optimistic counts.
   */
  @action.bound
  async fetchColumnsByKeys(
    keys: string[],
    version = this.claimColumnCounts(keys),
  ): Promise<void> {
    const loadedKeys = [...new Set(keys)].filter((key) =>
      this.columns.some((column) => column.key === key),
    );
    if (loadedKeys.length === 0) return;

    const boardKey = this.boardKey;
    const [data, error] = await this.columnCountsByKeysFetcher.fetch(
      {
        filters: this.countsQueryFilters,
        groupBy: this.groupBy,
        groupValues: loadedKeys,
      },
      // Not shared: a later refresh may cover other columns, so it must not
      // cancel this one. Out-of-order responses are handled by the versions.
      new AbortController().signal,
      false,
      true,
    );

    if (error || !data.taskBoardColumnCounts) return;
    // Status and user ids overlap — never apply counts to another board.
    if (boardKey !== this.boardKey) return;

    this.updateColumnCounts(
      this.toColumnSources(data.taskBoardColumnCounts.data),
      version,
    );
  }

  @action
  private updateColumnCounts(
    sources: TaskBoardColumnSource[],
    version: number,
  ): void {
    for (const source of sources) {
      if (!this.isLatestColumnCounts(source.key, version)) continue;

      this.columns.find((column) => column.key === source.key)?.update(source);
    }
  }

  /**
   * Set how many columns one page holds, from the available width.
   */
  @action.bound
  setColumnBatchSizeByWidth(width: number): void {
    if (width <= 0) return;

    const newColumnBatchSize = Math.max(
      1,
      Math.ceil(width / BOARD_COLUMN_WIDTH),
    );
    if (newColumnBatchSize === this.columnBatchSize) return;

    this.columnBatchSize = newColumnBatchSize;
    // A wider viewport needs more columns right away; a narrower one keeps
    // what is already loaded so the board never unmounts under the user.
    if (newColumnBatchSize <= this.filtersStore.pagination.perPage) return;

    // Pages are offsets of the page size — a bigger page on the old page
    // number would skip columns, so start over with the new size.
    this.fetchColumns({ init: true });
  }

  /**
   * The horizontal loader scrolled into view — pull the next page. The only
   * guard for it: `fetchColumns` itself skips while a page is still loading.
   */
  @action.bound
  async loadMoreColumns(): Promise<void> {
    if (!this.canLoadMoreColumns) return;
    await this.fetchColumns();
  }

  @computed
  get sortOptions(): { column: TaskOrderByColumnEnum; label: string }[] {
    // Order follows the prototype: Manual · Deadline · Name · Priority · Created at.
    return [
      { column: TaskOrderByColumnEnum.BoardOrder, label: t`Manual` },
      { column: TaskOrderByColumnEnum.Deadline, label: t`Deadline` },
      { column: TaskOrderByColumnEnum.TaskName, label: t`Name` },
      { column: TaskOrderByColumnEnum.TaskPriority, label: t`Priority` },
      { column: TaskOrderByColumnEnum.CreatedAt, label: t`Created at` },
    ];
  }

  @computed
  get sortText(): string {
    const column = this.settingsStore.sort.column;
    return (
      this.sortOptions.find((option) => option.column === column)?.label ?? ""
    );
  }

  @computed
  get isSortedBy() {
    return (column: TaskOrderByColumnEnum) =>
      this.settingsStore.sort.column === column;
  }

  @computed
  get groupByText() {
    return {
      [TaskBoardGroupByEnum.TaskStatus]: t`Status`,
      [TaskBoardGroupByEnum.TaskPriority]: t`Priority`,
      [TaskBoardGroupByEnum.Assignee]: t`Assignee`,
      [TaskBoardGroupByEnum.CurrentAssignee]: t`Current Assignee`,
    }[this.groupBy];
  }

  @computed
  get groupByOptions(): { value: TaskBoardGroupByEnum; label: string }[] {
    const options = [
      { value: TaskBoardGroupByEnum.TaskStatus, label: t`Status` },
      { value: TaskBoardGroupByEnum.TaskPriority, label: t`Priority` },
      { value: TaskBoardGroupByEnum.Assignee, label: t`Assignee` },
    ];

    if (!this.appStore.workspaceStore.settings?.use_current_assignee) {
      return options;
    }
    // Every card on My current tasks has the same current assignee — you.
    if (this.tab === "my-current") return options;

    return [
      ...options,
      {
        value: TaskBoardGroupByEnum.CurrentAssignee,
        label: t`Current assignee`,
      },
    ];
  }

  /** What keeps cards from being moved right now — empty when they can be. */
  @computed
  get reorderBlockers(): BoardReorderBlocker[] {
    const blockers: BoardReorderBlocker[] = [];

    if (!this.settingsStore.isManualSort) blockers.push("sort");
    if (this.filtersStore.searchTerm.length > 0) blockers.push("search");
    if (
      this.filtersStore.where.visibleFilters.some(
        (filter) => filter.value.length > 0,
      )
    ) {
      blockers.push("filters");
    }

    return blockers;
  }

  /**
   * "Move to top" and "Move to bottom" still work while searching — you
   * search for a card buried deep in a column to bring it up. Both mean the
   * whole column, so the search does not change where the card lands.
   */
  @computed
  get edgeMoveBlockers(): BoardReorderBlocker[] {
    return this.reorderBlockers.filter((blocker) => blocker !== "search");
  }

  @computed
  get canReorderCards(): boolean {
    return this.reorderBlockers.length === 0;
  }

  @computed
  get reorderBlockedNotice(): BoardMoveBlockedNotice | null {
    return this.moveBlockedNotice(this.reorderBlockers);
  }

  @computed
  get edgeMoveBlockedNotice(): BoardMoveBlockedNotice | null {
    return this.moveBlockedNotice(this.edgeMoveBlockers);
  }

  /**
   * What breaks the moving rule right now — every active blocker, so one fix
   * does not just reveal the next — over the rule itself. Null when cards move.
   */
  private moveBlockedNotice(
    blockers: BoardReorderBlocker[],
  ): BoardMoveBlockedNotice | null {
    if (blockers.length === 0) return null;

    return {
      title: blockers
        .map((blocker) => this.reorderBlockerReason(blocker))
        .join(" "),
      description: t`Tasks can only be moved when sorting is set to Manual and no filters are active.`,
      actions: blockers.map((blocker) => ({
        label: this.reorderBlockerAction(blocker),
        onClick: () => this.clearReorderBlocker(blocker),
      })),
    };
  }

  reorderBlockerReason(blocker: BoardReorderBlocker): string {
    switch (blocker) {
      case "sort": {
        const sortText = this.sortText;
        return t`Cards are sorted by ${sortText}.`;
      }
      case "search":
        return t`Search is active.`;
      case "filters":
        return t`Filters are active.`;
    }
  }

  /** Label of the fix `clearReorderBlocker` applies. */
  reorderBlockerAction(blocker: BoardReorderBlocker): string {
    switch (blocker) {
      case "sort":
        return t`Switch to Manual`;
      case "search":
        return t`Clear search`;
      case "filters":
        return t`Clear filters`;
    }
  }

  /** Removes the blocker, so cards can be moved again. */
  @action.bound
  clearReorderBlocker(blocker: BoardReorderBlocker): void {
    switch (blocker) {
      case "sort":
        this.settingsStore.setSort(TaskOrderByColumnEnum.BoardOrder);
        return;
      case "search":
        this.filtersStore.setSearchInput("");
        this.filtersStore.handleSearchChange("");
        return;
      case "filters":
        this.filtersStore.where.clearAllVisibleFilters();
        return;
    }
  }

  @action.bound
  showMoveBlockedToast({
    title,
    description,
    actions,
  }: BoardMoveBlockedNotice): void {
    this.appStore.UIStore.toast({
      status: "warning",
      title,
      description,
      actions,
      isClosable: true,
    });
  }

  /** The task's current value of the grouped field; null when empty. */
  taskGroupValue(taskId: string, column: TaskBoardColumnStore): string | null {
    if (this.groupBy === TaskBoardGroupByEnum.Assignee) {
      return this.assigneeColumnUserId(column);
    }

    const task = column.tasks.find((card) => card.id === taskId);
    if (!task) return this.columnGroupValue(column);

    switch (this.groupBy) {
      case TaskBoardGroupByEnum.TaskStatus:
        return task.status.id;
      case TaskBoardGroupByEnum.TaskPriority:
        return task.priority.id;
      case TaskBoardGroupByEnum.CurrentAssignee:
        return task.currentAssignee?.id ?? null;
      default:
        return this.columnGroupValue(column);
    }
  }

  assigneeColumnUserId(column: TaskBoardColumnStore): string | null {
    if (this.groupBy !== TaskBoardGroupByEnum.Assignee) return null;
    if (column.key === BOARD_UNASSIGNED_VALUE) return null;

    return column.key;
  }

  @computed
  get isRefreshingCards(): boolean {
    return this.visibleColumns.some((column) => column.isRefreshing);
  }

  /**
   * The board mounted for a tab. Another tab is another board; the same tab
   * again (back from the list view) reloads what the store still holds.
   */
  @action.bound
  activate(tab: TTaskPageVariant): void {
    if (this.tab !== tab) {
      this.setTab(tab);
      return;
    }

    void this.refreshColumns();
  }

  @action.bound
  setTab(tab: TTaskPageVariant): void {
    if (this.tab === tab) return;
    this.switchBoard(() => {
      this.tab = tab;
    });
    this.clearSelection();
    void this.fetchColumns({ init: true });
  }

  @action.bound
  setGroupBy(groupBy: TaskBoardGroupByEnum): void {
    if (this.groupBy === groupBy) return;
    this.switchBoard(() => {
      this.selectedGroupBy = groupBy;
      this.persistGroupBy(groupBy);
    });
    this.clearSelection();
    // New columns load their cards once they scroll into view.
    void this.fetchColumns({ init: true });
  }

  @action.bound
  toggleSelectMode(): void {
    this.isSelectMode = !this.isSelectMode;
    if (!this.isSelectMode) this.clearSelection();
  }

  /** `Done` in the bulk bar and `Escape` both land here. */
  @action.bound
  exitSelectMode(): void {
    if (!this.isSelectMode) return;
    this.isSelectMode = false;
    this.clearSelection();
  }

  @action.bound
  private clearSelection(): void {
    this.selectAnchor = undefined;
    this.bulkActionsStore.handleCancelSelection();
  }

  isTaskSelected(taskId: string): boolean {
    return this.bulkActionsStore.isSelected(taskId);
  }

  /**
   * In select mode a click anywhere on the card toggles the selection instead
   * of opening the detail. Shift-click selects the range from the last
   * clicked card, but only within one column — a range across columns has no
   * defined order on a board (phase 4.7).
   */
  @action.bound
  handleCardClick(
    taskId: string,
    column: TaskBoardColumnStore,
    shiftKey: boolean,
  ): void {
    if (this.dragStore.isDragReleaseClick()) return;

    if (!this.isSelectMode) {
      this.handleTaskClick(taskId);
      return;
    }

    if (shiftKey && this.selectAnchor?.columnKey === column.key) {
      const ids = column.tasks.map((task) => task.id);
      const from = ids.indexOf(this.selectAnchor.taskId);
      const to = ids.indexOf(taskId);

      if (from !== -1 && to !== -1) {
        const [start, end] = from < to ? [from, to] : [to, from];
        this.bulkActionsStore.setSelectedIds([
          ...this.bulkActionsStore.selectedIds,
          ...ids.slice(start, end + 1),
        ]);
        return;
      }
    }

    this.bulkActionsStore.handleSelect(taskId, []);
    this.selectAnchor = { columnKey: column.key, taskId };
  }

  /** The board pages over columns, not over rows — one page is one viewport. */
  resetPagination(): void {
    this.filtersStore.pagination.setFromPaginatorInfo({
      perPage: this.columnBatchSize,
      currentPage: 1,
      total: undefined,
      firstItem: undefined,
      lastItem: undefined,
      lastPage: undefined,
      count: undefined,
    });
  }

  /**
   * Cards again from their first page — after a sort change the loaded ones
   * are in the old order. Columns on screen replace them in place; collapsed
   * or hidden ones drop them and load with the new order once shown.
   */
  @action.bound
  async fetchTasks(): Promise<void> {
    const loadedColumns = this.columns.filter((column) => column.hasLoadedOnce);
    const isShown = (column: TaskBoardColumnStore) =>
      !column.isCollapsed && !this.settingsStore.isHidden(column.key);

    for (const column of loadedColumns) {
      if (!isShown(column)) column.reset();
    }

    await Promise.all(
      loadedColumns.filter(isShown).map((column) => column.reload()),
    );
  }

  @action.bound
  async fetchVisibleColumnTasks(keys?: string[]): Promise<void> {
    await Promise.all(
      this.visibleColumns
        .filter((column) => !column.isCollapsed && column.hasLoadedOnce)
        .filter((column) => !keys || keys.includes(column.key))
        .map((column) => column.reload()),
    );
  }

  columnQueryFilters(
    column: TaskBoardColumnStore,
  ): TaskBoardColumnTasksQueryVariables["filters"] {
    const filters = this.baseQueryFilters;
    const columnCondition = this.columnWhereCondition(column);

    return {
      ...filters,
      where: {
        AND: [...(filters.where?.AND ?? []), ...columnCondition],
      },
      orderBy: [
        // My tracked task opens its column, whatever the sort.
        { column: TaskOrderByColumnEnum.IsTracking, order: SortOrder.Asc },
        {
          column: this.settingsStore.sort.column,
          order: this.settingsStore.sort.direction,
          board_key: this.boardKey,
        },
      ],
    };
  }

  @action.bound
  async moveTaskOnBoard(
    input: Omit<MoveTaskOnBoardInput, "board_key" | "group_by">,
    columnKeys: string[],
  ): Promise<boolean> {
    const [, error] = await this.moveTaskMutator.mutate({
      input: {
        ...input,
        board_key: this.boardKey,
        group_by: this.groupBy,
      },
    });

    if (error) return false;

    await this.refreshColumns(columnKeys);
    return true;
  }

  @action.bound
  handleAddTask(
    column: TaskBoardColumnStore,
    placement: "top" | "bottom",
  ): void {
    this.appStore.taskFormModalStore.modalState.onOpen({
      ...this.columnTaskDefaults(column),
      onSubmit: (task) => this.handleTaskCreated(task.id, column, placement),
    });
  }

  updateTask(
    updatedTask: { id: string } & Partial<ListingInternalTaskFragment>,
  ): void {
    for (const column of this.columns) {
      column.updateTask({ id: updatedTask.id, name: updatedTask.name });
    }
  }

  removeTask(taskId: string): void {
    for (const column of this.columns) {
      column.removeTask(taskId);
    }
  }

  handleTaskClick(taskId: string): void {
    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: taskId,
      // The detail edits any field, so any loaded column may be affected.
      onChange: () => this.refreshColumns(this.columnKeys),
      onDelete: () => this.refreshColumns(this.columnKeys),
      onDuplicate: () => this.refreshColumns(this.columnKeys),
    });
  }

  @action.bound
  patchBoardTask(taskId: string, updates: Partial<TaskBaseFragment>): void {
    for (const column of this.columns) {
      column.updateTask({ id: taskId, ...updates });
    }
  }

  /**
   * A timer stopped — the card's tracked time and its columns' spent time
   * come from the API, so they lag until patched here.
   */
  @action.bound
  updateTaskStats(taskId: string, stats: TaskBaseFragment["stats"]): void {
    const columnKeys = this.columnKeysOfTask(taskId);
    if (columnKeys.length === 0) return;

    this.patchBoardTask(taskId, { stats });
    void this.fetchColumns({ keys: columnKeys });
  }

  /**
   * Group-by field changed — move the card into the matching column instead
   * of refetching every loaded column.
   */
  @action.bound
  private moveBoardTaskToColumn(
    task: TaskBaseFragment,
    destColumnKey: string,
  ): void {
    const source = this.columns.find((column) =>
      column.tasks.some((card) => card.id === task.id),
    );
    const dest = this.columns.find((column) => column.key === destColumnKey);

    if (!source || source.key === dest?.key) return;

    source.removeTask(task.id);
    void this.fetchColumns({ keys: [source.key, destColumnKey] });
    if (!dest) return;

    if (dest.hasLoadedOnce) {
      dest.prependTask(task);
      return;
    }

    dest.adjustTotals(task, 1);
  }

  @action.bound
  async toggleTaskStar(task: TaskBaseFragment): Promise<void> {
    const starred = !task.is_starred;
    this.patchBoardTask(task.id, { is_starred: starred });

    const [, error] = starred
      ? await this.appStore.starStore.star(task.id, StarsStarableTypeEnum.Task)
      : await this.appStore.starStore.unstar(
          task.id,
          StarsStarableTypeEnum.Task,
        );

    if (error) this.patchBoardTask(task.id, { is_starred: task.is_starred });
  }

  @action.bound
  async renameTask(task: TaskBaseFragment, name: string): Promise<void> {
    const trimmedName = name.trim();
    if (!trimmedName || trimmedName === task.name) return;

    // The editor closes on commit, so the card shows the new name right away
    this.patchBoardTask(task.id, { name: trimmedName });
    const [, error] = await this.updateTaskMutator.mutate({
      input: {
        id: task.id,
        name: trimmedName,
        is_template: task.is_template,
      },
      propagate_billable: false,
    });
    if (error) {
      this.patchBoardTask(task.id, { name: task.name });
      return;
    }

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Task name successfully updated`,
    });
  }

  @action.bound
  async updateTaskPriority(
    task: TaskBaseFragment,
    priorityId: string,
  ): Promise<void> {
    const [data, error] = await this.updateTaskPriorityMutator.mutate({
      task_id: task.id,
      priority_id: priorityId,
    });
    if (error || !data) return;

    const priority = data.updateTaskPriority.priority;
    this.patchBoardTask(task.id, { priority });

    if (this.groupBy !== TaskBoardGroupByEnum.TaskPriority) return;

    this.moveBoardTaskToColumn({ ...task, priority }, priority.id);
  }

  @action.bound
  async changeTaskStatus(
    task: TaskBaseFragment,
    taskStatusId: string,
  ): Promise<void> {
    const [, error] = await this.updateTaskMutator.mutate({
      input: {
        id: task.id,
        task_status_id: taskStatusId,
        is_template: task.is_template,
      },
      propagate_billable: false,
    });
    if (error) return;

    if (this.groupBy === TaskBoardGroupByEnum.TaskStatus) {
      // The task's own status names its column even when the card is past
      // the loaded pages there.
      await this.refreshColumns([
        ...this.columnKeysOfTask(task.id),
        this.statusColumnKey(task.status.id),
        this.statusColumnKey(taskStatusId),
      ]);
      return;
    }

    const status = this.filtersStore.taskStatuses.find(
      (taskStatus) => taskStatus.id === taskStatusId,
    );
    if (!status) {
      await this.refreshColumns(this.columnKeysOfTask(task.id));
      return;
    }

    this.patchBoardTask(task.id, { status });
  }

  /**
   * Patches the card in place, unless the change moves it between columns of
   * the current grouping — then the affected columns are refetched.
   */
  async handleTaskAssigneesChanged(
    task: TaskBaseFragment,
    updatedTask: { id: string } & Partial<TaskBaseFragment>,
  ): Promise<void> {
    const groupKeys = (value: TaskBaseFragment): string[] => {
      if (this.groupBy === TaskBoardGroupByEnum.CurrentAssignee) {
        return [value.currentAssignee?.id ?? BOARD_UNASSIGNED_VALUE];
      }

      const assigneeIds = this.appStore.tasksStore
        .assignees(value)
        .map((user) => user.id);
      return assigneeIds.length ? assigneeIds : [BOARD_UNASSIGNED_VALUE];
    };

    const isGroupedByAssignee =
      this.groupBy === TaskBoardGroupByEnum.CurrentAssignee ||
      this.groupBy === TaskBoardGroupByEnum.Assignee;
    const oldKeys = isGroupedByAssignee ? groupKeys(task) : [];
    const newKeys = isGroupedByAssignee
      ? groupKeys({ ...task, ...updatedTask })
      : [];

    if (oldKeys.join() === newKeys.join()) {
      this.patchBoardTask(task.id, updatedTask);
      return;
    }

    await this.refreshColumns([
      ...new Set([...this.columnKeysOfTask(task.id), ...newKeys]),
    ]);
  }

  @action.bound
  confirmTaskDelete(taskId: string): void {
    const modalId = `delete-board-task-${taskId}`;
    this.appStore.UIStore.dialogs.openModal({
      id: modalId,
      content: createElement(ModalConfirm, {
        destructive: true,
        isOpen: true,
        heading: t`Delete task`,
        message: t`Are you sure you want to delete this task?`,
        confirmBtnTitle: t`Delete`,
        onClose: () => this.appStore.UIStore.dialogs.closeModal(modalId),
        onConfirm: async () => {
          await this.deleteTask(taskId);
          this.appStore.UIStore.dialogs.closeModal(modalId);
        },
      }),
    });
  }

  @action.bound
  async deleteTask(taskId: string): Promise<void> {
    const [, error] = await this.deleteTaskMutator.mutate({ id: taskId });
    if (error) return;

    this.removeTask(taskId);
  }

  @action.bound
  duplicateTask(taskId: string): void {
    this.appStore.UIStore.dialogs.openModal({
      content: createElement(DuplicateTaskModal, {
        taskId,
        onDuplicate: () => this.refreshColumns(this.columnKeys),
      }),
    });
  }

  /** Only the move dialog — the task detail stays closed. */
  @action.bound
  openMoveModal(task: TaskBaseFragment): void {
    const modalId = `move-board-task-${task.id}`;
    this.appStore.UIStore.dialogs.openModal({
      id: modalId,
      content: createElement(ModalTaskEdit, {
        isOpen: true,
        mode: FormMode.MOVE,
        taskId: task.id,
        filterParentTasksForGroup: Boolean(task.task_template_group_id),
        priorityOptions: this.appStore.workspaceStore.taskPriorityOptions,
        statusOptions: this.filtersStore.taskStatuses.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        onClose: () => this.appStore.UIStore.dialogs.closeModal(modalId),
        // A move changes the project, so any loaded column may be affected
        onUpdate: () => this.refreshColumns(this.columnKeys),
      }),
    });
  }

  /**
   * Kebab reorder, optimistic like a drag: the card moves at once and goes
   * back on an error. Blocked by the same guard as drag — clicking a blocked
   * move shows the board alert instead of silently doing nothing (6.6).
   * The column may not have its last cards loaded: "bottom" lets the API
   * find the end, and "down" from the last loaded card loads the next page
   * for its neighbor.
   */
  @action.bound
  async moveCardInColumn(
    taskId: string,
    column: TaskBoardColumnStore,
    placement: "top" | "up" | "down" | "bottom",
  ): Promise<void> {
    const blockedNotice =
      placement === "top" || placement === "bottom"
        ? this.edgeMoveBlockedNotice
        : this.reorderBlockedNotice;
    if (blockedNotice !== null) {
      this.showMoveBlockedToast(blockedNotice);
      return;
    }

    if (this.movingTaskId !== null) return;

    const index = column.tasks.findIndex((task) => task.id === taskId);
    if (index === -1) return;

    const isFirst = index === 0;
    if ((placement === "top" || placement === "up") && isFirst) return;
    if (
      (placement === "down" || placement === "bottom") &&
      column.isLastTask(taskId)
    ) {
      return;
    }

    if (placement === "down" && index === column.tasks.length - 1) {
      this.movingTaskId = taskId;
      try {
        await column.fetchTasks();
      } finally {
        runInAction(() => {
          this.movingTaskId = null;
        });
      }
      // The next page failed to load — there is no neighbor to move below.
      if (index === column.tasks.length - 1) return;
    }

    const toBottom = placement === "bottom";
    // Past the loaded pages the card only leaves the list — it shows up again
    // once the paging reaches the end of the column.
    const leavesLoadedPages = toBottom && !column.isFullyLoaded;
    const snapshot = column.snapshot();

    if (leavesLoadedPages) {
      column.detachTask(taskId);
    } else {
      column.moveTaskToIndex(
        taskId,
        {
          top: 0,
          up: index - 1,
          down: index + 1,
          bottom: column.tasks.length - 1,
        }[placement],
      );
    }

    const { afterTaskId, beforeTaskId } = toBottom
      ? { afterTaskId: null, beforeTaskId: null }
      : this.moveAnchors(
          column.tasks.filter((task) => task.id !== taskId),
          column.tasks.findIndex((task) => task.id === taskId),
        );

    const [, error] = await this.moveTaskMutator.mutate({
      input: {
        task_id: taskId,
        // The task's own value, not the column key — the Complete column
        // groups several done statuses and must not rewrite them.
        group_value: this.taskGroupValue(taskId, column),
        source_assignee_user_id: this.assigneeColumnUserId(column),
        before_task_id: beforeTaskId,
        after_task_id: afterTaskId,
        to_bottom: toBottom,
        board_key: this.boardKey,
        group_by: this.groupBy,
      },
    });

    if (error) {
      column.restore(snapshot);
      return;
    }

    this.appStore.UIStore.toast({
      status: "success",
      title: {
        top: t`Task moved to the top`,
        up: t`Task moved up`,
        down: t`Task moved down`,
        bottom: t`Task moved to the bottom`,
      }[placement],
    });

    if (leavesLoadedPages) void column.resync();
  }

  isTrackingTask(taskId: string): boolean {
    return this.appStore.timeTrackingStore.trackingForTask(taskId);
  }

  /**
   * Manual-order neighbors of a card placed at `insertIndex` of `tasks` — the
   * column without that card. My tracked task is pinned to the top outside
   * the manual order, so it never anchors a move.
   */
  moveAnchors(
    tasks: TaskBaseFragment[],
    insertIndex: number,
  ): { afterTaskId: string | null; beforeTaskId: string | null } {
    const isAnchor = (task: TaskBaseFragment) => !this.isTrackingTask(task.id);

    return {
      afterTaskId: tasks.slice(0, insertIndex).findLast(isAnchor)?.id ?? null,
      beforeTaskId: tasks.slice(insertIndex).find(isAnchor)?.id ?? null,
    };
  }

  cardElapsedSeconds(taskId: string): number | null {
    if (!this.isTrackingTask(taskId)) return null;

    const entry = this.appStore.timeTrackingStore.runningEntry;
    if (!entry) return null;

    if (this.trackingStartedAtMs == null) {
      return entry.tracked_time_with_running_timer;
    }

    tickingNow();
    return (
      entry.tracked_time +
      Math.floor((Date.now() - this.trackingStartedAtMs) / 1000)
    );
  }

  canTrackTask(task: TaskBaseFragment): boolean {
    if (!task.ourWorkBudgetItem?.timeTrackingSettings.tracking_enabled) {
      return false;
    }

    const myId = this.appStore.authStore.user?.id;
    return task.positions.some(
      (position) =>
        position.user?.id === myId && !!position.timeTrackingWorkType,
    );
  }

  @action.bound
  handleTaskTrackingChange(isRunning: boolean): void {
    this.trackingStartedAtMs = isRunning ? Date.now() : null;
  }

  /**
   * A move without `after`/`before` lands on the top of the column, so the
   * bottom placement must not call it — a fresh task sorts to the bottom of the
   * manual order on its own (implicit rank `id * gap`).
   */
  private async handleTaskCreated(
    taskId: string,
    column: TaskBoardColumnStore,
    placement: "top" | "bottom",
  ): Promise<void> {
    if (placement === "top") {
      // The move puts the task into this column, wherever the form left it.
      await this.moveTaskOnBoard(
        {
          task_id: taskId,
          group_value: this.columnGroupValue(column),
          before_task_id: this.moveAnchors(column.tasks, 0).beforeTaskId,
        },
        [column.key],
      );
      return;
    }

    // The create form has no current assignee field, so set it separately.
    if (this.groupBy === TaskBoardGroupByEnum.CurrentAssignee) {
      await this.updateCurrentAssigneeMutator.mutate({
        taskId,
        userId: this.columnGroupValue(column),
      });
    }

    // The form may have put the task anywhere — status, priority, assignees.
    await this.refreshColumns(this.columnKeys);
  }

  private columnTaskDefaults(
    column: TaskBoardColumnStore,
  ): TaskFormModalOpenOptions {
    const columnValue = this.columnGroupValue(column);
    if (!columnValue) return {};

    switch (this.groupBy) {
      case TaskBoardGroupByEnum.TaskStatus:
        return { taskStatusId: columnValue };
      case TaskBoardGroupByEnum.TaskPriority:
        return { taskPriorityId: columnValue };
      case TaskBoardGroupByEnum.Assignee:
        return { userId: columnValue };
      default:
        return {};
    }
  }

  /** `null` is the "no value" column; the API also accepts `unassigned`. */
  columnGroupValue(column: TaskBoardColumnStore): string | null {
    if (column.key === BOARD_UNASSIGNED_VALUE) return null;
    if (column.key !== BOARD_COMPLETE_COLUMN_KEY) return column.key;

    return column.statusIds?.[0] ?? null;
  }

  @computed
  private get baseQueryFilters(): TaskBoardColumnTasksQueryVariables["filters"] {
    const { where, search } = this.filtersStore.queryParams.filters;

    return { where, search };
  }

  /**
   * Every column query adds its own group-by condition, and the API ignores
   * `ONLY_PARENT` once any real condition is set — so column cards include
   * subtasks. The counts drop it too, or they would count parents only.
   */
  @computed
  private get countsQueryFilters(): TaskBoardColumnTasksQueryVariables["filters"] {
    const { where, search } = this.baseQueryFilters;

    return {
      search,
      where: {
        AND: where?.AND?.filter(
          (filter) =>
            !("column" in filter) ||
            filter.column !== TaskWhereColumn.OnlyParent,
        ),
      },
    };
  }

  /** The counts filters except show done tasks, which the reaction handles alone. */
  @computed
  private get filtersKeyWithoutHideDone(): string {
    const { where, search } = this.countsQueryFilters;

    return JSON.stringify({
      search,
      where: where?.AND?.filter(
        (filter) =>
          !("column" in filter) || filter.column !== TaskWhereColumn.HideDone,
      ),
    });
  }

  /**
   * Done tasks only ever sit in the complete column, so showing or hiding
   * them leaves every other status column as it is.
   */
  @action.bound
  private async refreshCompleteColumn(): Promise<void> {
    const column = this.columns.find(
      ({ key }) => key === BOARD_COMPLETE_COLUMN_KEY,
    );
    // Not loaded yet — the next page of columns brings it in the right state.
    if (!column) return;

    // Disabled drops its cards, even collapsed; once enabled, the column loads
    // its first page itself as soon as it is shown.
    if (column.isDisabled) column.reset();

    await this.refreshColumns([column.key]);
  }

  private columnWhereCondition(column: TaskBoardColumnStore) {
    const columnValue =
      column.key === BOARD_COMPLETE_COLUMN_KEY
        ? (column.statusIds ?? [])
        : [column.key];

    const columnMap: Record<TaskBoardGroupByEnum, TaskWhereColumn> = {
      [TaskBoardGroupByEnum.TaskStatus]: TaskWhereColumn.TaskStatusId,
      [TaskBoardGroupByEnum.TaskPriority]: TaskWhereColumn.TaskPriorityId,
      [TaskBoardGroupByEnum.Assignee]: TaskWhereColumn.UserId,
      [TaskBoardGroupByEnum.CurrentAssignee]: TaskWhereColumn.CurrentAssigneeId,
    };

    return [
      {
        column: columnMap[this.groupBy],
        operator: SqlOperator.In,
        value: columnValue,
      },
    ];
  }

  @action
  private setColumns(
    sources: TaskBoardColumnSource[],
    options?: { append?: boolean; version?: number },
  ): void {
    const existingColumns = new Map(
      this.columns.map((column) => [column.key, column]),
    );
    const version = options?.version;

    const pageColumns = sources.map((source) => {
      const existingColumn = existingColumns.get(source.key);
      if (!existingColumn) return new TaskBoardColumnStore(this, source);

      // A newer keyed refresh owns this column's counts — keep them.
      if (
        version !== undefined &&
        !this.isLatestColumnCounts(source.key, version)
      ) {
        return existingColumn;
      }

      existingColumn.update(source);
      return existingColumn;
    });

    if (options?.append) {
      const pageKeys = new Set(pageColumns.map((column) => column.key));

      this.columns = [
        ...this.columns.filter((column) => !pageKeys.has(column.key)),
        ...pageColumns,
      ];
    } else {
      this.columns = pageColumns;
    }
  }

  private switchBoard(update: () => void): void {
    update();
    // Column keys overlap between boards (status "5" vs user "5") — the old
    // stores must not be reused for the new grouping.
    this.columns = [];
    this.resetPagination();
  }

  /**
   * The API already merged the done statuses into the `complete` column and
   * put the columns in the saved order — only the labels are ours: the complete
   * column takes the done status name, the fallbacks are translated here as
   * the API has no locale.
   */
  private toColumnSources(
    counts: TaskBoardColumnCountsQuery["taskBoardColumnCounts"]["data"],
  ): TaskBoardColumnSource[] {
    return counts.map((count) => {
      const key = count.group_value ?? BOARD_UNASSIGNED_VALUE;
      const isCompleteColumn = key === BOARD_COMPLETE_COLUMN_KEY;

      return {
        key,
        label: isCompleteColumn
          ? this.completeColumnLabel
          : (count.group_label ?? t`None`),
        total: count.total,
        spentTime: count.spent_time,
        plannedTime: count.planned_time,
        statusIds: isCompleteColumn ? this.doneStatusIds : undefined,
        ...this.columnColors(key),
      };
    });
  }

  private columnColors(columnKey: string): Partial<TaskBoardColumnSource> {
    if (this.groupBy !== TaskBoardGroupByEnum.TaskStatus) return {};

    const status = this.filtersStore.taskStatuses.find(
      ({ id }) => id === columnKey,
    );
    if (!status) return {};

    return {
      backgroundColor: status.background_color,
      foregroundColor: status.foreground_color,
    };
  }

  private readPersistedGroupBy(): TaskBoardGroupByEnum {
    try {
      const persistedGroupBy = localStorage.getItem(
        BOARD_GROUP_BY_STORAGE_KEY,
      ) as TaskBoardGroupByEnum | null;

      if (!persistedGroupBy) return TaskBoardGroupByEnum.TaskStatus;
      if (!Object.values(TaskBoardGroupByEnum).includes(persistedGroupBy)) {
        return TaskBoardGroupByEnum.TaskStatus;
      }

      return persistedGroupBy;
    } catch (_error) {
      return TaskBoardGroupByEnum.TaskStatus;
    }
  }

  private persistGroupBy(groupBy: TaskBoardGroupByEnum): void {
    try {
      localStorage.setItem(BOARD_GROUP_BY_STORAGE_KEY, groupBy);
    } catch (_error) {}
  }
}
